/**
 * Polling stand-in for Firestore's onSnapshot: list views re-fetch through a
 * callable every few seconds. The first fetch is immediate, requests never
 * overlap, and polling pauses while the tab is hidden (refetching as soon as
 * it is visible again).
 */

export type Unsubscribe = () => void;

export const POLL_INTERVAL_MS = 5_000;

export interface Visibility {
  isHidden(): boolean;
  /** Calls `onChange` on every visibility change; returns a remover. */
  subscribe(onChange: () => void): () => void;
}

/** The page's visibility, or "always visible" outside a browser (tests). */
export function documentVisibility(): Visibility {
  if (typeof document === 'undefined') {
    return { isHidden: () => false, subscribe: () => () => {} };
  }
  return {
    isHidden: () => document.hidden,
    subscribe(onChange) {
      document.addEventListener('visibilitychange', onChange);
      return () => document.removeEventListener('visibilitychange', onChange);
    },
  };
}

export function poll<T>(
  fetcher: () => Promise<T>,
  onUpdate: (value: T) => void,
  onError?: (err: Error) => void,
  opts: { intervalMs?: number; visibility?: Visibility } = {},
): Unsubscribe {
  const visibility = opts.visibility ?? documentVisibility();
  let stopped = false;
  let inFlight = false;

  async function tick(): Promise<void> {
    if (stopped || inFlight || visibility.isHidden()) return;
    inFlight = true;
    try {
      const value = await fetcher();
      if (!stopped) onUpdate(value);
    } catch (err) {
      if (!stopped) onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      inFlight = false;
    }
  }

  void tick();
  const timer = setInterval(() => void tick(), opts.intervalMs ?? POLL_INTERVAL_MS);
  const removeListener = visibility.subscribe(() => {
    if (!visibility.isHidden()) void tick();
  });

  return () => {
    stopped = true;
    clearInterval(timer);
    removeListener();
  };
}
